import React from 'react';
import { Field } from 'react-final-form';
import MultiSelectAutocomplete from '@/components/FormFields/MultiSelectAutocomplete';
import NumberTextField from '@/components/FormFields/NumberTextField';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FieldError } from '@/ui';
import NativeSelectField from '../FormFields/NativeSelectField';
import { renderToggleSwitch } from '../FormFields/reduxFormRenderers';

export const ToggleSwitchRow = ({ label, name }) => (
  <Stack direction='row' spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
    <FormLabel>{label}</FormLabel>
    <Field name={name} component={renderToggleSwitch} />
  </Stack>
);

export function MultiselectField({
  data,
  name,
  busy = false,
  parse = (value) => value,
  format = (value) => value,
  textField,
  dataKey,
  groupBy,
  renderListGroup,
  allowCreate = false,
  placeholder = 'Select',
}) {
  return (
    <Field name={name} parse={parse} format={format}>
      {({ input, meta }) => (
        <>
          <MultiSelectAutocomplete
            name={input.name}
            options={data}
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            placeholder={placeholder}
            busy={busy}
            textField={textField}
            dataKey={dataKey ? dataKey : 'name'}
            groupBy={groupBy}
            renderListGroup={renderListGroup}
            allowCreate={allowCreate}
            error={Boolean(meta.touched && meta.error)}
          />
          <FieldError error={meta.error} touched={meta.touched} />
        </>
      )}
    </Field>
  );
}

export function SelectField({
  name,
  options = [{ value: '', option: 'Select' }],
  parse = (value) => value,
  format = (value) => value,
}) {
  return (
    <Field name={name} parse={parse} format={format}>
      {({ input, meta }) => (
        <div>
          <NativeSelectField {...input} placeholder='Select'>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.option}
              </option>
            ))}
          </NativeSelectField>
          <FieldError error={meta.error} touched={meta.touched} />
        </div>
      )}
    </Field>
  );
}

export function SelectRow({
  name,
  label,
  options = [
    { value: '', option: 'None' },
    { value: 'Include', option: 'Include' },
    { value: 'Exclude', option: 'Exclude' },
  ],
  children,
}) {
  return (
    <Stack direction='row' spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
      <Typography variant='subtitle1' sx={{ minWidth: 160 }}>
        {label}
      </Typography>
      <div style={{ minWidth: 200 }}>
        <SelectField name={name} options={options} />
      </div>
      {children}
    </Stack>
  );
}

export const Condition = ({ when, is, children }) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => (is.includes(value) ? children : null)}
  </Field>
);

export const ColCondition = ({ when, children }) => (
  <div>
    <Field name={when} subscription={{ value: true }}>
      {({ input: { value } }) => (value && value.toLowerCase() !== 'none' && value !== null ? children : null)}
    </Field>
  </div>
);

export function NumberField({ name, label, min = 0, noLabel = false }) {
  return (
    <Field name={name} format={(value) => parseInt(value)}>
      {({ input, meta }) => {
        return (
          <>
            {!noLabel && <FormLabel>{label}</FormLabel>}
            <NumberTextField
              name={input.name}
              min={min}
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              placeholder={label}
              error={Boolean(meta.touched && meta.error)}
            />
            <FieldError error={meta.error} touched={meta.touched} />
          </>
        );
      }}
    </Field>
  );
}

export function PercentageField({ name, label, noLabel = false, allowNegative = false, inlineLabel = false }) {
  return (
    <Field name={name} format={(value) => parseFloat(value)}>
      {({ input, meta }) => {
        const value = typeof input.value === 'string' ? parseFloat(input.value) || 0 : input.value;
        const field = (
          <NumberTextField
            percent
            name={input.name}
            placeholder={label}
            min={allowNegative ? -1 : 0}
            max={1}
            step={0.01}
            value={value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            error={Boolean(meta.touched && meta.error)}
          />
        );
        return (
          <>
            {inlineLabel ? (
              <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
                <FormLabel sx={{ mb: 0 }}>{label}</FormLabel>
                <div style={{ flex: 1 }}>{field}</div>
              </Stack>
            ) : (
              <>
                {!noLabel && <FormLabel>{label}</FormLabel>}
                {field}
              </>
            )}
            <FieldError error={meta.error} touched={meta.touched} />
          </>
        );
      }}
    </Field>
  );
}

export function TextAreaField({ name, label }) {
  return (
    <Field name={name}>
      {({ input, meta }) => {
        return (
          <>
            <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
              <FormLabel sx={{ minWidth: 160, mt: 1 }}>{label}</FormLabel>
              <div style={{ flex: 1 }}>
                <TextField multiline minRows={3} {...input} placeholder={label} />
                <FieldError error={meta.error} touched={meta.touched} />
              </div>
            </Stack>
          </>
        );
      }}
    </Field>
  );
}
